import { useState } from "react";

import { hasValidRutDigits } from "@/lib/rut";
import { ArrowLeft, ArrowRight, Check, Loader2, Search } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useOnboardingBlocked } from "@/pages/onboarding/OnboardingStep";
import toast from "react-hot-toast";

import { OnboardingAfterTrail } from "@/components/onboarding/OnboardingAfterTrail";
import { OnboardingBrandColumn } from "@/components/onboarding/OnboardingBrandColumn";
import { OnboardingBrandMark } from "@/components/onboarding/OnboardingBrandMark";
import { OnboardingDgiFeed } from "@/components/onboarding/OnboardingDgiFeed";
import { OnboardingShell } from "@/components/onboarding/OnboardingShell";
import { OnboardingStepTag } from "@/components/onboarding/OnboardingStepTag";
import { cn } from "@/lib/utils";
import { validateRut, type CompanyTypeValue } from "@/services/authService";
import { translateAuthErrorMessage } from "@/utils/translateAuthErrorMessage";

const RUC_DIGITS = 12;

function normalizeRut(value: string): string {
  return value.replace(/\D/g, "").padStart(RUC_DIGITS, "0").slice(0, RUC_DIGITS);
}

export interface VerifyCompanyCompanyData {
  legalName?: string;
  companyType?: CompanyTypeValue;
  address?: string;
  city?: string;
  department?: string;
  phone?: string;
  contactEmail?: string;
}

export interface VerifyCompanyStepProps {
  onCreateCompany: (
    rut: string,
    companyData?: VerifyCompanyCompanyData,
  ) => Promise<void>;
  onBack?: () => void;
}

const COMPANY_TYPE_LABEL: Record<CompanyTypeValue, string> = {
  unipersonal: "Unipersonal",
  sas: "SAS",
  srl: "SRL",
  sa: "SA",
  monotributo: "Monotributo",
};

function formatLookupTimestamp(date: Date, locale: string): string {
  const dateStr = date.toLocaleDateString(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const timeStr = date.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${dateStr} · ${timeStr}`;
}

export function VerifyCompanyStep({
  onCreateCompany,
  onBack,
}: VerifyCompanyStepProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const reportBlocked = useOnboardingBlocked();
  const [rut, setRut] = useState("");
  const [legalName, setLegalName] = useState<string | null>(null);
  const [legalAddress, setLegalAddress] = useState("");
  const [city, setCity] = useState("");
  const [department, setDepartment] = useState("");
  const [phone, setPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [companyType, setCompanyType] = useState<CompanyTypeValue | "">("");
  const [validating, setValidating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lookupAt, setLookupAt] = useState<Date | null>(null);

  const normalizedRut = normalizeRut(rut);
  // Checked against the RAW input: normalizeRut pads to 12, so testing its
  // output always passed and the lookup button was enabled on an empty field.
  const rutValid = hasValidRutDigits(rut);
  const verified = Boolean(legalName);
  const canContinue = verified && contactEmail.trim() !== "" && phone.trim() !== "";

  const handleValidateRut = async (): Promise<void> => {
    if (!rutValid) {
      reportBlocked("rut_format_invalid");
      toast.error(t("onboarding.verifyCompany.rutFormatError"));
      return;
    }
    setValidating(true);
    try {
      const result = await validateRut(normalizedRut);
      setLegalName(result.legalName ?? normalizedRut);
      setCompanyType(result.suggestedCompanyType ?? "");
      setLegalAddress(result.legalAddress ?? "");
      setCity(result.city ?? "");
      setDepartment(result.department ?? "");
      setPhone(result.phone ?? "");
      setContactEmail(result.email ?? "");
      setLookupAt(new Date());
      toast.success(t("onboarding.verifyCompany.rutValidated"));
    } catch (err: unknown) {
      const status =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { status?: number } }).response?.status
          : undefined;
      const data =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data
          : undefined;
      const msg = data?.message as string | undefined;
      const message =
        status === 409
          ? t("onboarding.verifyCompany.rutAlreadyRegistered")
          : status === 503
            ? t("onboarding.verifyCompany.rutUnavailable")
            : translateAuthErrorMessage(msg, t) ??
              t("onboarding.verifyCompany.rutInvalid");
      reportBlocked("rut_validation_rejected");
      toast.error(message);
    } finally {
      setValidating(false);
    }
  };

  const handleContinue = async (): Promise<void> => {
    if (!canContinue) return;
    setSubmitting(true);
    try {
      await onCreateCompany(normalizedRut, {
        legalName: legalName ?? undefined,
        companyType: companyType || undefined,
        address: legalAddress.trim() || undefined,
        city: city.trim() || undefined,
        department: department.trim() || undefined,
        phone: phone.trim() || undefined,
        contactEmail: contactEmail.trim() || undefined,
      });
    } catch (err: unknown) {
      const data =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data
          : undefined;
      const msg = data?.message as string | undefined;
      reportBlocked("company_create_failed");
      toast.error(
        translateAuthErrorMessage(msg, t) ?? t("onboarding.verifyCompany.error"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const tipoGiro =
    companyType && COMPANY_TYPE_LABEL[companyType]
      ? COMPANY_TYPE_LABEL[companyType]
      : t("onboarding.step2.tipoGiroFallback", "—");

  return (
    <OnboardingShell
      form={
        <>
          <OnboardingBrandMark />
          <OnboardingStepTag
            step={2}
            label={t("onboarding.step2.tagLabel", "Datos de DGI")}
          />
          <h1 className="fx-onb-h1">
            {t("onboarding.step2.titleLead", "Buscamos tu")}{" "}
            <span className="ital">
              {t("onboarding.step2.titleAccent", "empresa.")}
            </span>
          </h1>
          <p className="fx-onb-lead">
            {t(
              "onboarding.step2.lead",
              "Ingresá el RUT — 12 dígitos — y Fiscalo trae todos los datos directo desde DGI. No vas a tener que tipearlos.",
            )}
          </p>

          <div className="fx-onb-form">
            <div className="fx-onb-form-row with-btn">
              <div className="fx-onb-field" style={{ gridColumn: "span 2" }}>
                <label htmlFor="verify-company-rut">
                  {t("onboarding.verifyCompany.rutLabel", "RUT de la empresa")}
                </label>
                <input
                  id="verify-company-rut"
                  type="text"
                  inputMode="numeric"
                  placeholder={t(
                    "onboarding.verifyCompany.rutPlaceholder",
                    "000000000000",
                  )}
                  value={rut}
                  onChange={(e) => {
                    setRut(e.target.value);
                    if (verified) {
                      setLegalName(null);
                      setLookupAt(null);
                    }
                  }}
                  maxLength={RUC_DIGITS + 4}
                  readOnly={verified}
                  disabled={submitting}
                />
              </div>
              <button
                type="button"
                className={cn("fx-onb-btn-validate", verified && "success")}
                onClick={handleValidateRut}
                disabled={validating || submitting || !rutValid || verified}
              >
                {validating ? (
                  <>
                    <Loader2 className="animate-spin" aria-hidden />
                    {t("onboarding.verifyCompany.validating", "Buscando…")}
                  </>
                ) : verified ? (
                  <>
                    <Check aria-hidden />
                    {t("onboarding.step2.verifiedButton", "Verificado")}
                  </>
                ) : (
                  <>
                    <Search aria-hidden />
                    {t("onboarding.step2.searchButton", "Buscar")}
                  </>
                )}
              </button>
            </div>

            {verified ? (
              <>
                <div className="fx-onb-dgi-lookup">
                  <span className="ico-tile">
                    <Check aria-hidden />
                  </span>
                  <div className="body">
                    <div className="title">
                      {t(
                        "onboarding.step2.dgiLookupTitle",
                        "Encontramos tu empresa en DGI",
                      )}
                    </div>
                    <div className="meta">
                      {t("onboarding.step2.dgiLookupConsulted", "Consultado")} ·{" "}
                      {lookupAt
                        ? formatLookupTimestamp(lookupAt, i18n.language)
                        : ""}
                    </div>
                  </div>
                  <span className="tag">
                    {t("onboarding.step2.dgiLookupTag", "Activa")}
                  </span>
                </div>

                <div className="fx-onb-verified-block">
                  <div className="vb-head">
                    <span className="title">
                      {t("onboarding.step2.verifiedTitleLead", "Datos")}{" "}
                      <span className="ital">
                        {t("onboarding.step2.verifiedTitleAccent", "de DGI.")}
                      </span>
                    </span>
                    <span className="verified">
                      <span className="dot">
                        <Check aria-hidden />
                      </span>
                      {t("onboarding.step2.verifiedPill", "Verificado")}
                    </span>
                  </div>
                  {/* data-mag-private: razón social, tipo de giro and the
                      rest of the DGI-returned fields. Magig masks form-control
                      text by default but not rendered text nodes. */}
                  <div className="vb-grid" data-mag-private>
                    <div className="fx-onb-vb-item">
                      <div className="k">
                        {t("onboarding.step2.razonSocial", "Razón social")}
                      </div>
                      <div className="v">{legalName}</div>
                    </div>
                    <div className="fx-onb-vb-item">
                      <div className="k">
                        {t("onboarding.step2.tipoGiro", "Tipo")}
                      </div>
                      <div className="v">{tipoGiro}</div>
                    </div>
                    <div className="fx-onb-vb-item">
                      <div className="k">
                        {t("onboarding.step2.direccion", "Dirección fiscal")}
                      </div>
                      <div className="v">
                        {legalAddress ||
                          t("onboarding.step2.direccionFallback", "—")}
                      </div>
                    </div>
                    <div className="fx-onb-vb-item">
                      <div className="k">
                        {t("onboarding.step2.departamento", "Departamento")}
                      </div>
                      <div className="v">
                        {department ||
                          city ||
                          t("onboarding.step2.departamentoFallback", "—")}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="fx-onb-form" style={{ marginTop: 8 }}>
                  <div className="fx-onb-field">
                    <label>
                      {t(
                        "onboarding.step2.contactSectionLabel",
                        "Necesitamos también",
                      )}
                    </label>
                  </div>
                  <div className="fx-onb-form-row">
                    <div className="fx-onb-field">
                      <label htmlFor="verify-company-contactEmail">
                        {t(
                          "onboarding.step2.contactEmailLabel",
                          "Correo de contacto",
                        )}
                      </label>
                      <input
                        id="verify-company-contactEmail"
                        type="email"
                        autoComplete="email"
                        placeholder="vos@email.com"
                        value={contactEmail}
                        onChange={(e) => setContactEmail(e.target.value)}
                        disabled={submitting}
                      />
                    </div>
                    <div className="fx-onb-field">
                      <label htmlFor="verify-company-phone">
                        {t("onboarding.step2.contactPhoneLabel", "Teléfono")}
                      </label>
                      <input
                        id="verify-company-phone"
                        type="tel"
                        placeholder="099 000 000"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        disabled={submitting}
                      />
                    </div>
                  </div>
                </div>
              </>
            ) : null}
          </div>

          <div className="fx-onb-actions">
            <div className="side">
              {onBack ? (
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-secondary"
                  onClick={onBack}
                  disabled={submitting}
                >
                  <ArrowLeft aria-hidden />
                  {t("onboarding.back", "Volver")}
                </button>
              ) : null}
            </div>
            <div className="side">
              <button
                type="button"
                className="fx-onb-btn fx-onb-btn-primary"
                onClick={handleContinue}
                disabled={!canContinue || submitting}
              >
                {submitting ? (
                  <Loader2 className="animate-spin" aria-hidden />
                ) : (
                  <>
                    {t("onboarding.continue", "Continuar")}
                    <ArrowRight aria-hidden />
                  </>
                )}
              </button>
            </div>
          </div>
        </>
      }
      brand={
        <OnboardingBrandColumn
          eyebrow={t("onboarding.step2.brandEyebrow", "Fiscalo busca por vos")}
          quote={
            <>
              {t(
                "onboarding.step2.brandQuoteLead",
                "No tipeás dos veces.",
              )}{" "}
              <span className="accent">
                {t(
                  "onboarding.step2.brandQuoteAccent",
                  "Traemos tus datos",
                )}
              </span>{" "}
              {t("onboarding.step2.brandQuoteTail", "directo desde DGI.")}
            </>
          }
          currentStep={2}
          afterTrail={<OnboardingAfterTrail />}
          extra={
            <div className="fx-onb-value-block">
              <div
                className="vb-head"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <span>
                  {t(
                    "onboarding.step2.feedHead",
                    "Consultando DGI",
                  )}
                </span>
                <span style={{ color: "var(--fx-accent-300)" }}>
                  {t("onboarding.step2.feedLive", "En vivo")}
                </span>
              </div>
              <OnboardingDgiFeed
                items={
                  verified
                    ? [
                        {
                          label: t(
                            "onboarding.step2.feed1",
                            "RUT verificado en padrón",
                          ),
                          when: t("onboarding.step2.feed1When", "hace 2s"),
                        },
                        {
                          label: (
                            <>
                              {t(
                                "onboarding.step2.feed2Lead",
                                "Razón social:",
                              )}{" "}
                              <b data-mag-private>{legalName ?? ""}</b>
                            </>
                          ),
                          when: t("onboarding.step2.feed2When", "hace 2s"),
                        },
                        {
                          label: t(
                            "onboarding.step2.feed3",
                            "Domicilio fiscal sincronizado",
                          ),
                          when: t("onboarding.step2.feed3When", "hace 1s"),
                        },
                        {
                          label: t(
                            "onboarding.step2.feed4",
                            "Estado de actividad confirmado",
                          ),
                          when: t("onboarding.step2.feed4When", "ahora"),
                        },
                      ]
                    : [
                        {
                          label: t(
                            "onboarding.step2.feedIdle",
                            "Esperando tu RUT para consultar…",
                          ),
                          when: t("onboarding.step2.feedIdleWhen", "listo"),
                          spinning: true,
                        },
                      ]
                }
              />
            </div>
          }
        />
      }
    />
  );
}
